import { createTheme } from "@mui/material/styles";

const brandPrimary = {
  main: "#1e3a8a",
  dark: "#1b347b",
  light: "#2563eb",
  contrastText: "#ffffff",
};

const brandSecondary = {
  main: "#22d3ee",
  dark: "#0891b2",
  light: "#67e8f9",
  contrastText: "#0b1020",
};

function makeTheme(mode: "light" | "dark") {
  const isDark = mode === "dark";
  const background = isDark
    ? { default: "#0b1020", paper: "#0f172a" }
    : { default: "#f6f8fb", paper: "#ffffff" };
  const text = isDark
    ? {
        primary: "rgba(255,255,255,0.95)",
        secondary: "rgba(255,255,255,0.72)",
        disabled: "rgba(255,255,255,0.5)",
      }
    : {
        primary: "rgba(12, 18, 32, 0.95)",
        secondary: "rgba(12, 18, 32, 0.72)",
        disabled: "rgba(12, 18, 32, 0.45)",
      };

  const divider = isDark ? "rgba(255,255,255,0.12)" : "rgba(12,18,32,0.12)";

  return createTheme({
    palette: {
      mode,
      primary: brandPrimary,
      secondary: brandSecondary,
      success: { main: "#10b981" },
      warning: { main: "#f59e0b" },
      error: { main: "#ef4444" },
      info: { main: "#38bdf8" },
      background,
      text,
      divider,
    },
    shape: {
      borderRadius: 14,
    },
    typography: {
      fontFamily: [
        "Inter",
        "Segoe UI",
        "Roboto",
        "Helvetica",
        "Arial",
        "sans-serif",
      ].join(","),
      h6: { fontWeight: 800 },
      button: { fontWeight: 800, textTransform: "none" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: background.default,
            color: text.primary,
            backgroundImage: isDark
              ? "radial-gradient(1200px 600px at 100% -10%, rgba(37,99,235,0.08), transparent),radial-gradient(1000px 500px at -10% 110%, rgba(34,211,238,0.05), transparent)"
              : "radial-gradient(1200px 600px at 100% -10%, rgba(37,99,235,0.06), transparent),radial-gradient(1000px 500px at -10% 110%, rgba(34,211,238,0.04), transparent)",
          },
          '#full-ticket-content': {
            backgroundColor: background.paper,
            color: text.primary,
            border: `1px solid ${divider}`,
          },
          '#full-ticket-content .MuiTypography-root': { color: text.primary },
          '#full-ticket-content .MuiPaper-root': { backgroundColor: background.paper },
          '#full-ticket-content .MuiTypography-colorTextSecondary': { color: text.secondary },
          '#full-ticket-content .MuiDivider-root': { borderColor: divider },
          'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not(.MuiInputBase-input), textarea:not(.MuiInputBase-input), select:not(.MuiSelect-nativeInput)': {
            backgroundColor: background.paper,
            color: text.primary,
            border: `1px solid ${divider}`,
            borderRadius: 10,
            padding: "10px 12px",
            outline: "none",
            boxShadow: "none",
            transition: "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
          },
          'input:not(.MuiInputBase-input)::placeholder, textarea:not(.MuiInputBase-input)::placeholder': {
            color: text.disabled,
            opacity: 1,
          },
          'input:not(.MuiInputBase-input):focus, textarea:not(.MuiInputBase-input):focus, select:not(.MuiSelect-nativeInput):focus': {
            borderColor: brandPrimary.light,
            boxShadow: "0 0 0 3px rgba(37,99,235,0.22)",
          },
          'input:not(.MuiInputBase-input):disabled, textarea:not(.MuiInputBase-input):disabled, select:not(.MuiSelect-nativeInput):disabled': {
            backgroundColor: isDark ? "#0c1429" : "#eef2f7",
            color: isDark ? "rgba(255,255,255,0.55)" : "rgba(12,18,32,0.55)",
            borderColor: divider,
            cursor: "not-allowed",
          },
          'input, textarea, select': {
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
          },
          '@supports (-webkit-touch-callout: none)': {
            'input:not(.MuiInputBase-input):-webkit-autofill, textarea:not(.MuiInputBase-input):-webkit-autofill, select:not(.MuiSelect-nativeInput):-webkit-autofill': {
              WebkitTextFillColor: text.primary,
              boxShadow: `0 0 0px 1000px ${background.paper} inset`,
              transition: "background-color 5000s ease-in-out 0s",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? "rgba(255,255,255,0.8)" : "rgba(12,18,32,0.7)",
            '&.Mui-focused': { color: brandSecondary.main },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: background.paper,
            '& fieldset': { borderColor: divider },
            '&:hover fieldset': { borderColor: isDark ? "rgba(255,255,255,0.24)" : "rgba(12,18,32,0.2)" },
            '&.Mui-focused fieldset': { borderColor: brandPrimary.light },
          },
          input: {
            color: text.primary,
            '::placeholder': { color: text.disabled, opacity: 1 },
          },
        },
      },
      MuiSelect: {
        styleOverrides: { icon: { color: text.secondary } },
      },
      MuiFormHelperText: { styleOverrides: { root: { color: text.secondary } } },
      MuiTextField: { defaultProps: { variant: "outlined", size: "small", fullWidth: false } },
      MuiAppBar: { defaultProps: { color: "transparent" }, styleOverrides: { root: { backdropFilter: "saturate(120%) blur(8px)" } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { borderRadius: 14 } } },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: background.paper,
            border: `1px solid ${divider}`,
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          },
        },
      },
    },
  });
}

export const darkTheme = makeTheme("dark");
export const lightTheme = makeTheme("light");
export const getTheme = (mode: "light" | "dark") => (mode === "dark" ? darkTheme : lightTheme);

export default darkTheme;
